/**
 * enrichFeedDb.js
 * Dexie (IndexedDB) database for EnrichFeed / AcadFeed.
 *
 * Replaces localStorage for the three large stores that can exceed 5 MB:
 *   • feedItems   – cached RSS articles, keyed by guid
 *   • fetchTimes  – per-subject last-fetch timestamps
 *   • notes       – per-article user notes, keyed by guid
 *
 * Small scalar values (sortedScores, chosenTopSubject, etc.) stay in
 * localStorage – they are tiny and rarely cause quota issues.
 *
 * Usage:
 *   import { feedItemsStore, fetchTimesStore, notesStore } from './enrichFeedDb';
 */

import Dexie from 'dexie';

const db = new Dexie('EnrichFeedDB');

db.version(1).stores({
  /**
   * feedItems
   * Primary key: [userId+guid]  (compound – isolates users on shared devices)
   * Indexed: userId, subject  (for fast per-user / per-subject queries)
   */
  feedItems: '[userId+guid], userId, subject, pub_date',

  /**
   * fetchTimes
   * Primary key: [userId+subject]
   */
  fetchTimes: '[userId+subject], userId',

  /**
   * notes
   * Primary key: [userId+guid]
   */
  notes: '[userId+guid], userId',
});

// ─────────────────────────────────────────────
//  feedItems helpers
// ─────────────────────────────────────────────

/**
 * Load all feed items for a user as a { guid: item } map.
 * @param {string} userId
 * @returns {Promise<Object>}
 */
export async function loadFeedItems(userId) {
  const rows = await db.feedItems.where('userId').equals(userId).toArray();
  const map = {};
  rows.forEach((row) => {
    map[row.guid] = row;
  });
  return map;
}

/**
 * Upsert (put) an array of articles for a user.
 * Each article must have a `guid`.
 * @param {string} userId
 * @param {Object[]} articles
 */
export async function saveFeedItems(userId, articles) {
  const rows = articles.map((a) => ({ ...a, userId }));
  await db.feedItems.bulkPut(rows);
}

// ─────────────────────────────────────────────
//  fetchTimes helpers
// ─────────────────────────────────────────────

/**
 * Load all fetch timestamps for a user as { subject: timestamp } map.
 * @param {string} userId
 * @returns {Promise<Object>}
 */
export async function loadFetchTimes(userId) {
  const rows = await db.fetchTimes.where('userId').equals(userId).toArray();
  const map = {};
  rows.forEach((row) => {
    map[row.subject] = row.timestamp;
  });
  return map;
}

/**
 * Upsert the fetch timestamp for a single subject.
 * @param {string} userId
 * @param {string} subject
 * @param {number} timestamp  – Date.now()
 */
export async function saveFetchTime(userId, subject, timestamp) {
  await db.fetchTimes.put({ userId, subject, timestamp });
}

// ─────────────────────────────────────────────
//  notes helpers
// ─────────────────────────────────────────────

/**
 * Load all notes for a user as { guid: noteText } map.
 * @param {string} userId
 * @returns {Promise<Object>}
 */
export async function loadNotes(userId) {
  const rows = await db.notes.where('userId').equals(userId).toArray();
  const map = {};
  rows.forEach((row) => {
    map[row.guid] = row.text;
  });
  return map;
}

/**
 * Upsert a single note.
 * @param {string} userId
 * @param {string} guid
 * @param {string} text
 */
export async function saveNote(userId, guid, text) {
  await db.notes.put({ userId, guid, text });
}

/**
 * Load all AcadFeed notes for a user as raw rows (not the collapsed
 * guid→text map loadNotes returns) — used by the export engine.
 * @param {string} userId
 * @returns {Promise<Array<{userId: string, guid: string, text: string}>>}
 */
export async function getAllNotesForExport(userId) {
  return await db.notes.where('userId').equals(userId).toArray();
}

// ─────────────────────────────────────────────
//  One-time migration from localStorage → IndexedDB
//  Call this once on mount; it self-removes the old keys when done.
// ─────────────────────────────────────────────

/**
 * Migrates legacy localStorage data for this user into IndexedDB,
 * then removes the old localStorage keys to free the quota.
 * Safe to call on every mount – it's a no-op if the keys are absent.
 * @param {string} userId
 */
export async function migrateFromLocalStorage(userId) {
  // ── feed items ──────────────────────────────────────────────────────────
  const rawItems = localStorage.getItem(`enrichfeed_all_items_${userId}`);
  if (rawItems) {
    try {
      const itemMap = JSON.parse(rawItems); // { guid: article }
      const articles = Object.values(itemMap);
      if (articles.length > 0) {
        await saveFeedItems(userId, articles);
      }
      localStorage.removeItem(`enrichfeed_all_items_${userId}`);
      console.log(`[EnrichFeedDB] Migrated ${articles.length} feed items for user ${userId}`);
    } catch (e) {
      console.warn('[EnrichFeedDB] Could not migrate feed items:', e);
    }
  }

  // ── fetch timestamps ────────────────────────────────────────────────────
  const rawTimes = localStorage.getItem(`enrichfeed_fetch_times_${userId}`);
  if (rawTimes) {
    try {
      const timesMap = JSON.parse(rawTimes); // { subject: timestamp }
      for (const [subject, timestamp] of Object.entries(timesMap)) {
        await saveFetchTime(userId, subject, timestamp);
      }
      localStorage.removeItem(`enrichfeed_fetch_times_${userId}`);
      console.log('[EnrichFeedDB] Migrated fetch timestamps for user', userId);
    } catch (e) {
      console.warn('[EnrichFeedDB] Could not migrate fetch times:', e);
    }
  }

  // ── notes ───────────────────────────────────────────────────────────────
  const rawNotes = localStorage.getItem(`enrichfeed_notes_${userId}`);
  if (rawNotes) {
    try {
      const notesMap = JSON.parse(rawNotes); // { guid: text }
      for (const [guid, text] of Object.entries(notesMap)) {
        await saveNote(userId, guid, text);
      }
      localStorage.removeItem(`enrichfeed_notes_${userId}`);
      console.log('[EnrichFeedDB] Migrated notes for user', userId);
    } catch (e) {
      console.warn('[EnrichFeedDB] Could not migrate notes:', e);
    }
  }
}

/**
 * Bulk-restore AcadFeed notes from a backup (raw rows, same shape as
 * getAllNotesForExport returns). Upserts by [userId+guid].
 * @param {Array<{userId: string, guid: string, text: string}>} rows
 */
export async function bulkPutFeedNotes(rows) {
  if (!rows || rows.length === 0) return;
  await db.notes.bulkPut(rows);
}

export default db;
export { db as enrichFeedDb };